// Sessão de estudo do Ciclo: escolha de modo/assunto ao iniciar e registro ao finalizar.
// Extraído de App.js sem alteração de comportamento.
import { useState, useMemo } from "react";
import { editalLeaves, editalFlat, editalLangTopics, sessTopicIds, STUDY_MODES, REVIEW_MODES, ALL_MODES, RATINGS } from "../lib/edital";
import { useIsMobile, useTheme } from "../lib/theme";
import { NavIcon } from "./icons";
import { minutesToHuman } from "../lib/format";
import { SheetModal, Toggle } from "./ui";
import { EstrAulaList, Collapse, CheckBox, PickStyles } from "./EstrAulas";
import { estrFirstPending, estrBySlug as estrOf, sessAulaIds } from "../lib/estrategia";
import { loadLS, saveLS } from "../lib/storage";

// ── Edital verticalizado ─────────────────────────────────────────────────────────
// Conteúdo em src/editais.js (gerado dos PDFs oficiais), carregado sob demanda.
// ── Edital: store e helpers agora em ./lib/edital (importados no topo). ──


// Seleção em cascata: marcar um grupo marca tudo dentro dele; o cabeçalho fica marcado só quando
// todos os subitens estão marcados (desmarcar um subitem desmarca o cabeçalho, e completar o grupo marca de novo).
function syncGroups(tree, o) {
  const walk = n => {
    if (!(n.c && n.c.length)) return !!o[n.id];
    const all = n.c.map(walk).every(Boolean);
    if (all) o[n.id] = 1; else delete o[n.id];
    return all;
  };
  tree.forEach(walk);
  return o;
}
function toggleGroup(tree, prev, node) {
  const on = !prev[node.id];
  const o = { ...prev };
  [node, ...editalFlat(node.c || []).map(r => r.node)].forEach(n => { if (on) o[n.id] = 1; else delete o[n.id]; });
  return syncGroups(tree, o);
}

// Lista de assuntos (seleção múltipla no início e no fim da sessão).
// Os grupos começam fechados, só abre onde a pessoa está; tocar no título abre/fecha e a caixa marca/desmarca.
function TopicList({ d, fam, data, multi, selected, onToggle, planned }) {
  const t = useTheme();
  const done = (data && data.done) || {};
  const prog = (data && data.prog) || {};
  const tree = editalLangTopics(d, data, fam);
  const leavesOf = node => editalLeaves([node]);
  const isSel = id => multi ? !!selected[id] : selected === id;
  const plannedIds = Array.isArray(planned) ? planned : planned ? [planned] : [];
  const status = node => {
    const L = leavesOf(node);
    const n = L.filter(id => done[`${fam}:${d.id}:${id}`]).length;
    if (n === L.length) return "done";
    if (n > 0 || L.some(id => prog[`${fam}:${d.id}:${id}`]) || prog[`${fam}:${d.id}:${node.id}`]) return "doing";
    return "todo";
  };
  // Abre os grupos que têm algo escolhido/planejado; sem isso, o primeiro grupo com assunto pendente
  const [open, setOpen] = useState(() => {
    const keep = new Set([...Object.keys(multi ? (selected || {}) : (selected ? { [selected]: 1 } : {})), ...plannedIds]);
    const o = {};
    const walk = (n, trail) => {
      const here = [...trail, n];
      if (keep.has(n.id)) here.forEach(x => { if (x.c && x.c.length) o[x.id] = true; });
      (n.c || []).forEach(c => walk(c, here));
    };
    tree.forEach(n => walk(n, []));
    if (!Object.keys(o).length) {
      const first = tree.find(n => n.c && n.c.length && leavesOf(n).some(id => !done[`${fam}:${d.id}:${id}`]));
      if (first) o[first.id] = true;
    }
    return o;
  });
  const StatusDot = ({ s }) => s === "done"
    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Concluído"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.8 2.8L16.5 9.5" /></svg>
    : s === "doing"
      ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.ac} strokeWidth="2" strokeDasharray="3 3" aria-label="Em andamento"><circle cx="12" cy="12" r="9" /></svg>
      : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.textFaint} strokeWidth="1.6" aria-label="Pendente"><circle cx="12" cy="12" r="9" /></svg>;
  const renderRow = (node, depth) => {
    const kids = node.c && node.c.length ? node.c : null;
    const isOpen = !!open[node.id];
    const L = kids ? leavesOf(node) : null;
    const nSel = L ? L.filter(isSel).length : 0;
    const on = isSel(node.id);
    const st = status(node);
    const state = on ? "on" : (L && nSel > 0 ? "partial" : "off");
    const label = node.n ? `${node.n}${/^[IVX]+$/.test(node.n) ? " –" : "."} ${node.t}` : node.t;
    const isPlanned = plannedIds.includes(node.id);
    const flip = () => setOpen(o => ({ ...o, [node.id]: !o[node.id] }));
    const nDone = L ? L.filter(id => done[`${fam}:${d.id}:${id}`]).length : 0;
    return (
      <div key={node.id}>
        <div style={{ display:"flex", alignItems:"stretch", borderTop:`1px solid ${t.borderIn}`, background: state !== "off" ? t.acDim : (kids ? t.bgInner : "transparent"), transition:"background 0.18s" }}>
          <button onClick={() => onToggle(node)} aria-label={`${on ? "Desmarcar" : "Marcar"} ${label}`} role={multi ? "checkbox" : "radio"} aria-checked={on}
            style={{ flexShrink:0, border:"none", background:"transparent", cursor:"pointer", padding:`0.65rem 0.2rem 0.65rem ${0.8 + depth * 1.1}rem`, display:"flex", alignItems:"flex-start" }}>
            <span style={{ paddingTop:1 }}><CheckBox multi={multi} state={state} /></span>
          </button>
          <button onClick={kids ? flip : () => onToggle(node)} aria-expanded={kids ? isOpen : undefined}
            style={{ flex:1, minWidth:0, display:"flex", alignItems:"flex-start", gap:"0.6rem", textAlign:"left", cursor:"pointer", border:"none", background:"transparent", color:t.text, padding:"0.65rem 0.8rem 0.65rem 0.7rem" }}>
            <span style={{ flex:1, minWidth:0, fontSize: depth === 0 ? "0.86rem" : "0.82rem", lineHeight:1.45, color: st === "done" && state === "off" ? t.textDim : t.text, fontWeight: kids && depth === 0 ? 700 : 400 }}>{label}</span>
            <span style={{ display:"flex", alignItems:"center", gap:"0.45rem", flexShrink:0, paddingTop:1 }}>
              {isPlanned && <span style={{ fontSize:"0.62rem", fontWeight:800, letterSpacing:"0.06em", color:t.ac }}>PLANEJADO</span>}
              {kids
                ? <>
                    <span style={{ fontSize:"0.7rem", fontWeight:700, color: nDone === L.length ? t.ac : t.textDim }}>{nDone}/{L.length}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.textDim} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                      style={{ transform: isOpen ? "rotate(90deg)" : "none", transition:"transform 0.25s cubic-bezier(.4,0,.2,1)" }}><path d="M9 5l7 7-7 7" /></svg>
                  </>
                : <StatusDot s={st} />}
            </span>
          </button>
        </div>
        {kids && <Collapse open={isOpen}>{kids.map(c => renderRow(c, depth + 1))}</Collapse>}
      </div>
    );
  };
  return (
    <div className="g17-pick" style={{ border:`1px solid ${t.border}`, borderRadius:12, overflow:"hidden" }}>
      <PickStyles />
      {tree.map(n => renderRow(n, 0))}
    </div>
  );
}

function ModeCard({ m, on, onClick }) {
  const t = useTheme();
  return (
    <button onClick={onClick} aria-pressed={on}
      style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:"0.45rem", padding:"1rem 0.6rem", borderRadius:14, cursor:"pointer",
        background: on ? t.acDim : t.bgInner, border:`1.5px solid ${on ? t.ac : t.border}`, color:t.text, transition:"border-color 0.15s, background 0.15s" }}>
      <span style={{ width:48, height:48, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background: on ? t.ac : t.bgCard, color: on ? (t.mode === "dark" ? "#0a0f0b" : "#fff") : t.ac, border:`1px solid ${on ? t.ac : t.borderIn}` }}>
        <NavIcon id={m.icon} size={24} />
      </span>
      <span style={{ fontSize:"0.92rem", fontWeight:800 }}>{m.label}</span>
      <span style={{ fontSize:"0.72rem", color:t.textDim, lineHeight:1.35 }}>{m.desc}</span>
    </button>
  );
}

export function SessionStartModal({ kind, subjectName, d, fam, data, estr, onCancel, onStart }) {
  const t = useTheme();
  const modes = kind === "revisao" ? REVIEW_MODES : STUDY_MODES;
  const [mode, setMode] = useState(null);
  const firstPending = useMemo(() => {
    if (!d || kind === "revisao") return null;
    const done = (data && data.done) || {};
    const rows = editalFlat(editalLangTopics(d, data, fam));
    const r = rows.find(({ node }) => !(node.c && node.c.length) && !done[`${fam}:${d.id}:${node.id}`]);
    return r ? r.node.id : null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [topics, setTopics] = useState(() => (firstPending ? { [firstPending]: 1 } : {}));   // assuntos escolhidos (vários)
  // Flag do Estratégia (só a conta administradora recebe `estr`): aparece em Videoaula e PDF
  const showEstr = !!estr && kind !== "revisao" && (mode === "videoaula" || mode === "pdf");
  const [estrOn, setEstrOn] = useState(() => !!loadLS("sf_estrFlag", false));
  const [estrAulas, setEstrAulas] = useState(() => { const f = estr ? estrFirstPending(estr, data) : null; return f ? { [f]: 1 } : {}; });   // aulas escolhidas (várias)
  const flipEstr = v => { setEstrOn(v); saveLS("sf_estrFlag", v); };
  // Com o Estratégia ligado, o assunto do edital não vem marcado sozinho (a aula do curso nem sempre
  // corresponde a um assunto do edital): só vale o assunto que a pessoa escolher. Os dois são opcionais.
  const [topicTouched, setTopicTouched] = useState(false);
  const topicSel = showEstr && estrOn && !topicTouched ? {} : topics;
  // A sugestão (primeiro assunto / primeira aula pendente) já vem marcada. No primeiro toque, tocar na
  // sugestão a desmarca e tocar em outra item troca a sugestão por ele; depois disso cada toque marca ou desmarca.
  const toggleTopic = n => {
    const base = topicTouched ? { ...topicSel } : {};
    if (!topicTouched && topicSel[n.id]) { setTopicTouched(true); setTopics({}); return; }
    setTopicTouched(true); setTopics(toggleGroup(editalLangTopics(d, data, fam), base, n));
  };
  const [aulaTouched, setAulaTouched] = useState(false);
  const toggleAula = id => {
    const base = aulaTouched ? { ...estrAulas } : {};
    if (!aulaTouched && estrAulas[id]) { setAulaTouched(true); setEstrAulas({}); return; }
    if (base[id]) delete base[id]; else base[id] = 1;
    setAulaTouched(true); setEstrAulas(base);
  };
  const toggleManyAulas = (ids, on) => { setAulaTouched(true); setEstrAulas(cur => { const o = aulaTouched ? { ...cur } : {}; ids.forEach(id => { if (on) o[id] = 1; else delete o[id]; }); return o; }); };
  // ids na ordem do edital / do curso (o primeiro vira o "principal" do registro)
  const topicIds = d ? editalFlat(editalLangTopics(d, data, fam)).map(r => r.node.id).filter(id => topicSel[id]) : [];
  const aulaIds = estr ? estr.mods.flatMap(m => m.aulas.map(a => a.id)).filter(id => estrAulas[id]) : [];
  const sec = { fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:t.textDim, margin:"0 0 0.6rem" };
  return (
    <SheetModal labelledBy="g17-sess-start" onClose={onCancel}
      title={kind === "revisao" ? "Como você quer revisar?" : "Como você vai estudar?"}
      subtitle={<>Matéria: <strong style={{ color:t.text }}>{subjectName}</strong></>}
      footer={<>
        <button onClick={onCancel} style={{ background:"transparent", border:`1px solid ${t.border}`, borderRadius:10, padding:"0.65rem 1.1rem", color:t.textMid, cursor:"pointer", fontSize:"0.88rem" }}>Cancelar</button>
        <button onClick={() => mode && onStart({ mode, topic: topicIds[0] || null, topics: topicIds, estr: showEstr && estrOn ? { slug: estr.slug, aula: aulaIds[0] || null, aulas: aulaIds } : null })} disabled={!mode}
          style={{ background: mode ? t.ac : t.border, color: mode ? (t.mode === "dark" ? "#0a0f0b" : "#fff") : t.textFaint, border:"none", borderRadius:10, padding:"0.7rem 1.3rem", fontWeight:800, cursor: mode ? "pointer" : "not-allowed", fontSize:"0.9rem", display:"inline-flex", alignItems:"center", gap:"0.5rem" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          {kind === "revisao" ? "Iniciar revisão" : "Iniciar sessão"}
        </button>
      </>}>
      <p style={sec}>1. {kind === "revisao" ? "Modo de revisão" : "Modo de estudo"}</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:"0.6rem", marginBottom:"1.4rem" }}>
        {modes.map(m => <ModeCard key={m.id} m={m} on={mode === m.id} onClick={() => setMode(m.id)} />)}
      </div>
      {showEstr && (
        <div style={{ marginBottom:"1.4rem" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"0.8rem", padding:"0.8rem 0.9rem", borderRadius:12, background:t.bgInner, border:`1px solid ${estrOn ? t.ac : t.borderIn}` }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:"0.88rem", fontWeight:800, color:t.text }}>Estou usando o material do Estratégia</div>
              <div style={{ fontSize:"0.72rem", color:t.textDim, lineHeight:1.4, marginTop:2 }}>Escolha uma ou mais aulas do curso e marque o que assistiu ao finalizar.</div>
            </div>
            <Toggle on={estrOn} onChange={flipEstr} />
          </div>
          {estrOn && (
            <div style={{ marginTop:"0.7rem" }}>
              <p style={sec}>Aulas do Estratégia (opcional)</p>
              <p style={{ fontSize:"0.74rem", color:t.textDim, margin:"-0.2rem 0 0.6rem", lineHeight:1.45 }}>Marque todas as aulas que você pretende ver nesta sessão.</p>
              <EstrAulaList course={estr} data={data} multi selected={estrAulas} onToggle={toggleAula} onToggleMany={toggleManyAulas} maxHeight={300} />
              <p style={{ fontSize:"0.74rem", color:t.textDim, margin:"0.45rem 0 0" }}>{aulaIds.length === 0 ? "Nenhuma aula escolhida." : `${aulaIds.length} ${aulaIds.length === 1 ? "aula escolhida" : "aulas escolhidas"}.`}</p>
            </div>
          )}
        </div>
      )}
      <p style={sec}>2. Assuntos do edital{showEstr && estrOn ? " (opcional)" : ""}</p>
      {showEstr && estrOn && <p style={{ fontSize:"0.74rem", color:t.textDim, margin:"-0.2rem 0 0.6rem", lineHeight:1.45 }}>Escolha só se estas aulas correspondem a assuntos do edital. Pode ficar sem.</p>}
      {d ? (
        <>
          <div style={{ display:"flex", gap:"1rem", flexWrap:"wrap", fontSize:"0.74rem", color:t.textDim, marginBottom:"0.6rem" }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.ac} strokeWidth="2.2"><circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.8 2.8L16.5 9.5" /></svg>Concluído</span>
            <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.ac} strokeWidth="2.2" strokeDasharray="3 3"><circle cx="12" cy="12" r="9" /></svg>Em andamento</span>
            <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.textFaint} strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>Pendente</span>
          </div>
          <TopicList d={d} fam={fam} data={data} multi selected={topicSel} onToggle={toggleTopic} />
          <p style={{ fontSize:"0.74rem", color:t.textDim, margin:"0.45rem 0 0" }}>{topicIds.length === 0 ? "Nenhum assunto escolhido." : `${topicIds.length} ${topicIds.length === 1 ? "assunto escolhido" : "assuntos escolhidos"}.`}</p>
          <p style={{ fontSize:"0.72rem", color:t.textFaint, margin:"0.5rem 0 0", lineHeight:1.45 }}>
            <strong style={{ color:t.textDim }}>Concluído</strong>: você marcou como terminado ao finalizar uma sessão (ou na aba Edital). <strong style={{ color:t.textDim }}>Em andamento</strong>: você estudou e ainda não terminou, ou terminou só parte dos subitens. O assunto é opcional; toque de novo para desmarcar.
          </p>
        </>
      ) : (
        <div style={{ padding:"0.9rem 1rem", borderRadius:12, background:t.bgInner, border:`1px solid ${t.borderIn}`, color:t.textDim, fontSize:"0.82rem", lineHeight:1.5 }}>
          Esta matéria não tem assuntos no edital verticalizado (disponível para PRF e PF Agente, Escrivão e Papiloscopista). Você pode iniciar mesmo assim.
        </div>
      )}
    </SheetModal>
  );
}

export function SessionFinishModal({ session, subjectName, d, fam, data, measuredSec, plannedSec, onSave, onClose, onDiscard }) {
  const t = useTheme();
  const isMobTL = useIsMobile();
  const isRev = session.kind === "revisao";
  const [sel, setSel] = useState(() => {
    const ids = sessTopicIds(session);
    if (!d || !ids.length) return {};
    const flat = editalFlat(d.topics);
    const o = {};
    const planned = new Set(ids);
    ids.forEach(id => {
      const node = flat.find(r => r.node.id === id)?.node;
      if (!node) return;
      o[node.id] = 1;
      const kids = editalFlat(node.c || []).map(r => r.node);
      // grupo planejado junto com alguns subitens: vale só o que foi escolhido (não marca o resto do grupo)
      if (kids.some(n => planned.has(n.id))) return;
      kids.forEach(n => { o[n.id] = 1; });
    });
    syncGroups(editalLangTopics(d, data, fam), o);
    return o;
  });
  const [rating, setRating] = useState(null);
  // Material do Estratégia: a aula escolhida no início já vem marcada
  const estrCourse = session.estr ? (estrOf(session.estr.slug)) : null;
  const [selA, setSelA] = useState(() => Object.fromEntries(sessAulaIds(session.estr).map(id => [id, 1])));
  const toggleA = id => setSelA(prev => { const o = { ...prev }; if (o[id]) delete o[id]; else o[id] = 1; return o; });
  const toggleManyA = (ids, on) => setSelA(prev => { const o = { ...prev }; ids.forEach(id => { if (on) o[id] = 1; else delete o[id]; }); return o; });
  const measuredMin = Math.round(measuredSec / 60);
  const [adjMin, setAdjMin] = useState(null);     // minutos ajustados (null = usa o cronômetro)
  const minutes = adjMin !== null ? adjMin : measuredMin;
  const bump = d => setAdjMin(m => Math.max(0, Math.min(720, (m !== null ? m : measuredMin) + d)));
  const done = (data && data.done) || {};
  const toggle = node => setSel(prev => toggleGroup(editalLangTopics(d, data, fam), prev, node));
  const selLeaves = d ? editalLeaves(editalFlat(editalLangTopics(d, data, fam)).filter(r => sel[r.node.id] && !(r.node.c && r.node.c.length)).map(r => r.node)) : [];
  const newlyDone = selLeaves.filter(id => !done[`${fam}:${d.id}:${id}`]).length;
  const mode = ALL_MODES.find(m => m.id === session.mode);
  const sec = { fontSize:"0.95rem", fontWeight:800, color:t.text, margin:"0 0 0.3rem" };
  const sub = { fontSize:"0.8rem", color:t.textDim, margin:"0 0 0.7rem", lineHeight:1.45 };
  return (
    <SheetModal labelledBy="g17-sess-end" onClose={onClose}
      title={isRev ? "Revisão finalizada" : "Sessão finalizada"}
      subtitle={<>{subjectName}{mode ? <> · {mode.label}</> : null}</>}
      footer={<>
        <div style={{ display:"flex", gap:"0.9rem", alignItems:"center" }}>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:t.textMid, cursor:"pointer", fontSize:"0.88rem", padding:"0.6rem 0.4rem" }}>Fechar</button>
          {onDiscard && <button onClick={onDiscard} style={{ background:"transparent", border:"none", color:"#f87171", cursor:"pointer", fontSize:"0.88rem", padding:"0.6rem 0.4rem" }}>Descartar</button>}
        </div>
        <button onClick={() => onSave({ sel, leaves: selLeaves, rating, sec: adjMin === null ? measuredSec : minutes * 60, aulas: estrCourse ? Object.keys(selA) : [] })}
          style={{ background:t.ac, color: t.mode === "dark" ? "#0a0f0b" : "#fff", border:"none", borderRadius:10, padding:"0.75rem 1.4rem", fontWeight:800, cursor:"pointer", fontSize:"0.92rem" }}>Salvar registro</button>
      </>}>
      {estrCourse && (
        <>
          <p style={sec}>Aulas do Estratégia que você assistiu</p>
          <p style={sub}>A aula escolhida já vem marcada. Marque só o que você terminou. Não terminou? Desmarque: ela fica "Em andamento" e você continua na próxima sessão.</p>
          <EstrAulaList course={estrCourse} data={data} multi selected={selA} onToggle={toggleA} onToggleMany={toggleManyA} planned={sessAulaIds(session.estr)} maxHeight={320} />
          <p style={{ fontSize:"0.78rem", color:t.textDim, margin:"0.55rem 0 1.3rem" }}>
            {Object.keys(selA).length === 0 ? "Nenhuma aula será marcada como assistida." : `${Object.keys(selA).length} ${Object.keys(selA).length === 1 ? "aula será marcada" : "aulas serão marcadas"} como assistida${Object.keys(selA).length === 1 ? "" : "s"}.`}
          </p>
        </>
      )}
      {d ? (
        <>
          <p style={sec}>{isRev ? "Assuntos que você revisou" : "Assuntos que você finalizou"}</p>
          <p style={sub}>{isRev ? "Marque o que você revisou nesta sessão." : "Os assuntos planejados já vêm marcados. Marque só o que você terminou. Não terminou? Desmarque: ele fica \"Em andamento\" e você continua na próxima sessão."}</p>
          <TopicList d={d} fam={fam} data={data} multi selected={sel} onToggle={toggle} planned={sessTopicIds(session)} />
          {!isRev && <p style={{ fontSize:"0.78rem", color:t.textDim, margin:"0.55rem 0 1.3rem" }}>{newlyDone === 0 ? "Nenhum assunto novo será marcado como concluído." : newlyDone === 1 ? "1 assunto será marcado como concluído no edital." : `${newlyDone} assuntos serão marcados como concluídos no edital.`}</p>}
          {isRev && <div style={{ height:"1.1rem" }} />}
        </>
      ) : null}

      <p style={sec}>Tempo estudado</p>
      {(() => {
        const chip = (d, label) => (
          <button key={label} onClick={() => bump(d)} aria-label={`${d > 0 ? "Somar" : "Tirar"} ${Math.abs(d)} minuto${Math.abs(d) > 1 ? "s" : ""}`}
            style={{ minWidth: Math.abs(d) === 1 ? 40 : (isMobTL ? 42 : 46), height:40, borderRadius: Math.abs(d) === 1 ? "50%" : 10, border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, cursor:"pointer",
              fontSize: Math.abs(d) === 1 ? "1.2rem" : "0.86rem", fontWeight:600, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 0.5rem", flexShrink:0 }}>{label}</button>
        );
        return (
          <div style={{ padding:"0.9rem 0.8rem 0.8rem", borderRadius:12, background:t.bgInner, border:`1.5px solid ${adjMin !== null ? t.ac : t.borderIn}`, marginBottom:"1.3rem" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap: isMobTL ? "0.3rem" : "0.35rem", flexWrap:"wrap", rowGap:"0.6rem" }}>
              <div style={{ display:"flex", gap:"0.3rem", order: isMobTL ? 2 : 0 }}>{chip(-15, "−15")}{chip(-10, "−10")}{chip(-5, "−5")}</div>
              <div style={{ display:"flex", alignItems:"center", gap:"0.35rem", order: isMobTL ? 1 : 0, width: isMobTL ? "100%" : "auto", justifyContent:"center" }}>
                {chip(-1, "−")}
                <span className="tac-num" style={{ minWidth:100, textAlign:"center", fontSize:"1.5rem", fontWeight:800, color:t.text }}>{minutes}min</span>
                {chip(1, "+")}
              </div>
              <div style={{ display:"flex", gap:"0.3rem", order: isMobTL ? 3 : 0 }}>{chip(5, "+5")}{chip(10, "+10")}{chip(15, "+15")}</div>
            </div>
            <div style={{ textAlign:"center", fontSize:"0.78rem", color:t.textDim, marginTop:"0.6rem" }}>
              Cronômetro: {measuredSec < 60 ? "menos de 1 min" : `${measuredMin}min`}{plannedSec ? ` · planejado ${minutesToHuman(Math.round(plannedSec / 60))}` : ""}
              {adjMin !== null && adjMin > measuredMin && <div style={{ marginTop:"0.45rem", color:"#fbbf24", fontSize:"0.74rem", lineHeight:1.4 }}>O tempo adicionado vai para o calendário e para o ciclo, mas não conta no ranking.</div>}
              {adjMin !== null && adjMin !== measuredMin && <> · <button onClick={() => setAdjMin(null)} style={{ background:"none", border:"none", color:t.ac, cursor:"pointer", fontSize:"0.78rem", padding:0, textDecoration:"underline" }}>usar o cronômetro</button></>}
            </div>
          </div>
        );
      })()}

      <p style={sec}>Como você se avalia {d ? "nestes assuntos" : "nesta sessão"}?</p>
      <p style={sub}>A avaliação vale para os assuntos marcados e ajuda a planejar suas revisões.</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, minmax(0,1fr))", gap:"0.4rem" }}>
        {RATINGS.map(r => {
          const on = rating === r.v;
          return (
            <button key={r.v} onClick={() => setRating(on ? null : r.v)} aria-pressed={on}
              style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"0.3rem", padding:"0.7rem 0.25rem", borderRadius:12, cursor:"pointer",
                background: on ? `${r.color}22` : "transparent", border:`1.5px solid ${on ? r.color : t.border}`, color: on ? r.color : t.text, minWidth:0 }}>
              <NavIcon id={r.icon} size={26} />
              <span style={{ fontSize:"0.74rem", fontWeight:800, lineHeight:1.15, textAlign:"center" }}>{r.label}</span>
              <span style={{ fontSize:"0.62rem", color:t.textDim, lineHeight:1.25, textAlign:"center" }}>{r.desc}</span>
            </button>
          );
        })}
      </div>
    </SheetModal>
  );
}
